import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Wordmark } from "./Logo";
import { useLang } from "@/lib/i18n";
import { useScrollTo } from "./SmoothScroll";

const LINKS = [["tours", "#tours"], ["yachts", "#iates"], ["fleet", "#frota"], ["about", "#casa"], ["contact", "#contactos"]];

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center border border-[#C5A059]/30 rounded-full p-0.5 text-[11px] tracking-[0.2em]" data-testid="nav-language-toggle">
      {["pt", "en"].map((l) => (
        <button key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)}
          className={`px-3 py-1.5 rounded-full uppercase transition-colors duration-300 ${lang === l ? "bg-[#C5A059] text-black" : "text-zinc-300 hover:text-white"}`}>
          {l}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { t } = useLang();
  const go = useScrollTo();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const nav = (href) => { setOpen(false); go(href); };

  return (
    <motion.header initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 1, delay: 0.9 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${scrolled ? "bg-[#0A0A0C]/80 backdrop-blur-xl border-b border-white/5 py-3" : "py-6"}`}
      data-testid="site-header">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 flex items-center justify-between gap-6">
        <button onClick={() => nav("#topo")} data-testid="header-logo"><Wordmark /></button>
        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map(([k, h]) => (
            <button key={k} onClick={() => nav(h)} data-testid={`nav-${k}`} className="nav-link text-[13px] tracking-[0.18em] uppercase text-zinc-300 hover:text-white">
              {t.nav[k]}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <div className="hidden sm:block"><LangToggle /></div>
          <span className="hidden md:block"><button onClick={() => nav("#reservar")} data-testid="header-book-btn" className="btn-gold">{t.nav.book}</button></span>
          <button className="lg:hidden text-[#F9F9F9] p-2" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="lg:hidden mx-5 mt-3 rounded-2xl border border-[#C5A059]/20 bg-[#121216]/95 backdrop-blur-xl p-6 flex flex-col gap-5" data-testid="mobile-menu">
            {LINKS.map(([k, h]) => (
              <button key={k} onClick={() => nav(h)} className="text-left font-display text-3xl text-[#F9F9F9]" data-testid={`mobile-nav-${k}`}>{t.nav[k]}</button>
            ))}
            <div className="flex items-center justify-between pt-2">
              <LangToggle />
              <button onClick={() => nav("#reservar")} className="btn-gold" data-testid="mobile-book-btn">{t.nav.book}</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
